import { Injectable } from '@angular/core';
import { haversineMeters } from 'libs/shared/src/lib/geo/haversine.util';
import { CoordinateAdvancedMapHost } from './models/coordinate-map-context.model';

export interface GeoNodePoint {
  lat: number;
  lng: number;
  data: any;
  distanceMeters: number;
}

@Injectable({ providedIn: 'root' })
export class GeoNodeSearchService {
  findNearbyNodes(
    lat: number,
    lng: number,
    advancedMap: CoordinateAdvancedMapHost | null | undefined,
    radiusMeters = 300
  ): GeoNodePoint[] {
    if (!advancedMap?.findNearbyMarkers) {
      return [];
    }

    const radiusKm = radiusMeters / 1000;
    return advancedMap
      .findNearbyMarkers(lat, lng, radiusKm)
      .map((node) => ({
        lat: this.readLat(node, advancedMap),
        lng: this.readLng(node, advancedMap),
        data: node,
        distanceMeters: (node.distance ?? 0) * 1000,
      }));
  }

  findNearbyFromNodeList(
    lat: number,
    lng: number,
    nodes: any[],
    latField: string,
    lngField: string,
    radiusMeters = 300
  ): GeoNodePoint[] {
    if (!nodes?.length) {
      return [];
    }

    const results: GeoNodePoint[] = [];
    for (const node of nodes) {
      const nodeLat = Number(node[latField] ?? node.latitude ?? node.y);
      const nodeLng = Number(node[lngField] ?? node.longitude ?? node.x);
      if (!Number.isFinite(nodeLat) || !Number.isFinite(nodeLng)) {
        continue;
      }
      if (nodeLat === 0 && nodeLng === 0) {
        continue;
      }

      const distanceMeters = haversineMeters(lat, lng, nodeLat, nodeLng);
      if (distanceMeters <= radiusMeters) {
        results.push({ lat: nodeLat, lng: nodeLng, data: node, distanceMeters });
      }
    }

    return results.sort((a, b) => a.distanceMeters - b.distanceMeters);
  }

  private readLat(node: any, map: CoordinateAdvancedMapHost): number {
    const field = map.options?.latitudeField ?? 'y';
    return Number(node[field] ?? node.latitude ?? node.lat);
  }

  private readLng(node: any, map: CoordinateAdvancedMapHost): number {
    const field = map.options?.longitudeField ?? 'x';
    return Number(node[field] ?? node.longitude ?? node.lng);
  }
}
